/**
 * The comparison matrix and its allocation — `quoflow.quoflow.quoflow.api.comparison.*`,
 * Task 10 of the migration ("the task the whole migration was for"). Live shapes verified
 * 2026-08-04: `get_comparison`, `save_comparison` (POST — see `saveAllocation`), `build_quotation`.
 *
 * Follows the pattern `useEnquiries.ts`/`useSupplierQuotations.ts` set: a `Wire*` interface per
 * response, a pure exported `toX` converter, no shared mapper file.
 *
 * ---- replaces the OLD `Supplier Quotation` document assembly entirely ----
 *
 * The previous version of this file built `Comparison` client-side from `getDoc`/`getList`
 * against the raw `Supplier Quotation` doctype (`mappers/supplierQuotation.ts`'s `SQ_DOCTYPE`/
 * `toSupplierQuotation`/`toSqFxRate`) — a READ-ONLY workaround because `Supplier Quote
 * Comparison Item` has three FIXED supplier columns on the real schema (Q2) and one
 * `use_qty`/`selected_rate` pair per row (Q3, "our biggest problem": no multi-supplier split).
 * `get_comparison`/`save_comparison` are the real answer to both: an arbitrary supplier list
 * per item, and a flat `allocations` list that can hold several rows against the same
 * `item_code` — see `AllocationRow`'s own comment in `../types` and `lib/compare.ts`'s module
 * header for the full shape story. `mappers/supplierQuotation.ts` has no other caller left after
 * this file stops using it — retired in this same commit (task-9-report.md flagged it for this
 * task to finish).
 *
 * ---- one `items[]` row PER ENQUIRY LINE, not per item code ----
 *
 * The wire's `enquiry_row_no` is singular per item row, never a list — unlike the OLD
 * assembly, which grouped every `Supplier Quotation Item` sharing an `item_code` into one
 * `ComparisonItem` with several `ComparisonLine`s. This file does NOT re-aggregate: two
 * enquiry lines that happen to name the same item arrive as two independent `ComparisonItem`s,
 * each independently allocatable — matching what `AllocationRow` (keyed on `item_code` +
 * `enquiry_row_no` + `supplier`) can actually express. `ComparisonItem.lines` is kept as a
 * single-entry list (rather than dropped) purely so the existing UI (`WorkbenchTable`'s
 * `item.lines.map(...)`, the `#cust_sno` pill) keeps working unchanged.
 *
 * `cust_sno`/the customer's raw wording have no distinct field on this wire either (same gap
 * `useEnquiries.ts`'s `toEnquiryItem` documents for `get_enquiry`) — `enquiry_row_no` stands in
 * for `cust_sno`, and `item_name` (the MATCHED item's name, not the customer's own words)
 * stands in for the line description. Flagged for the backend follow-up list alongside that
 * file's own gap.
 *
 * ---- currency: offers carry no currency of their own ----
 *
 * `offers[code]` has no `currency` field — a supplier's rate is understood via
 * `ComparisonSupplier.currency` for that code, matched up here at conversion time
 * (`toComparisonItem`'s `currencyBySupplier`). Getting this wrong — labelling every offer with
 * one currency regardless of who quoted it — is exactly the mistake `lib/compare.ts`'s FX
 * exclusion rule exists to catch downstream; this is where the correct label gets attached in
 * the first place.
 *
 * ---- supplier identity ----
 *
 * `suppliers[].supplier` IS `Supplier.code` (task 8b) — never a display name. `offers` is keyed
 * by that same code. A caller wanting a label reads `supplier_name` off the matching
 * `ComparisonSupplier`, never the other way around.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { callGet, callPost } from './bosun'
import { createResource, getResource } from './frappe'
import { resolveEnquiryCompany } from './useEnquiryMutations'
import { advanceToComparisonDone } from './advanceStatus'
import { BosunApiError } from './errors'
import { bool01, money, num, str, trimmed, type WireFx } from './wire'
import { getBaseCurrency } from './baseCurrency'
import { qk } from './keys'
import { POLL_MS } from './polling'
import type { AllocationRow, Comparison, ComparisonItem, ComparisonSupplier, EnquiryStatus, Offer } from '../types'

/* ============================================================================================
 * comparison.get_comparison → useComparison
 * ============================================================================================ */

/**
 * An offer as the CURRENT server sends it: inside `items[].offers`, a map keyed by supplier code.
 *
 * The last five fields are undeclared no longer. They are on the live wire today — measured on
 * ENQ-2026-00018, 2026-08-08 — and were being dropped silently, while this file's header asserted
 * in bold that "offers carry no currency of their own". They do.
 */
export interface WireComparisonOffer {
  rate: number
  stock: number
  uom: string
  uom_mismatch: number | boolean
  remark?: string | null
  currency?: string | null
  conversion_rate?: number | null
  supplier_quotation?: string | null
  delivery_date?: string | null
  rate_in_base?: number | null
}

/**
 * An offer in the FLAT top-level `offers[]` array — the newer shape.
 *
 * The difference that matters is not cosmetic. Keyed by supplier, a map holds one offer per
 * supplier; live, ENQ-2026-00018 has five `Supplier Quotation`s under ONE supplier code and three
 * of them are unreachable through the map. Each row here carries its own `supplier_quotation`, so
 * every quotation survives.
 *
 * Joined back to its line by `enquiry_row_no` — NOT by `item_code`, which cannot tell two enquiry
 * lines naming the same item apart.
 */
export interface WireFlatOffer {
  item_code: string
  enquiry_row_no: string
  supplier: string
  supplier_name?: string | null
  supplier_quotation?: string | null
  rate: number
  currency?: string | null
  conversion_rate?: number | null
  rate_in_base?: number | null
  /** The newer name for `stock`. Both are read, so neither shape loses availability. */
  available_qty?: number | null
  stock?: number | null
  uom: string
  uom_mismatch: number | boolean
  delivery_date?: string | null
  remark?: string | null
}

export interface WireComparisonSupplier {
  supplier: string
  supplier_name?: string | null
  currency: string
  sq: string
}

export interface WireComparisonItem {
  item_code: string
  item_name?: string | null
  qty: number
  uom: string
  enquiry_row_no: string
  /** Present on the CURRENT shape only; the newer one puts offers in a flat top-level array. */
  offers?: Record<string, WireComparisonOffer | null>
  last_purchase_price?: number | null
  selected_rate?: number | null
  selected_supplier?: string | null
}

export interface WireAllocationRow {
  item_code: string
  enquiry_row_no: string
  supplier: string
  supplier_name?: string | null
  allocated_qty: number
  rate: number
  currency: string
  blended_rate_base?: number | null
}

export interface WireComparison {
  enquiry: string
  /** Absent on the newer shape, which pre-converts via `rate_in_base` instead. */
  fx?: WireFx
  /** Absent on the newer shape — derived from the offers themselves (see `suppliersFromOffers`). */
  suppliers?: WireComparisonSupplier[]
  items: WireComparisonItem[]
  /** The newer shape's flat offer list. Its presence is what selects the reader below. */
  offers?: WireFlatOffer[]
  allocations: WireAllocationRow[]
}

export function toComparisonSupplier(w: WireComparisonSupplier): ComparisonSupplier {
  const supplier = str(w.supplier)
  return {
    supplier,
    // Trimmed centrally by `wire.ts`'s `trimmed()` (task 12) — the live server returns a
    // leading space on `supplier_name`, same defect `useSuppliers.ts`/`useEnquiries.ts` trim.
    supplier_name: trimmed(w.supplier_name) || supplier,
    currency: str(w.currency, getBaseCurrency()),
    sq: str(w.sq),
  }
}

/**
 * A base-currency figure from the wire, or `null`.
 *
 * ---- `conversion_rate` is a UOM factor and is NOT read here ----
 *
 * This used to return `rate x conversion_rate`. It is the wrong field: `conversion_rate` is the
 * unit conversion — 1 packet to 1 pack is 1.0 — and carries no currency information whatever
 * (Akhil, 2026-09-04). Using it as an exchange rate is wrong in both directions:
 *
 *     conversion_rate 1.0  -> USD 50 read as AED 50    (understates by the entire FX rate)
 *     conversion_rate 24   -> USD 50 read as AED 1200  (a pack size applied as an exchange rate)
 *
 * That first case shipped: six USD offers on ENQ-2026-00101 ranked at 1/3.65 of their real cost,
 * one of them displaying as the cheapest line when it was in fact the dearest. An intermediate fix
 * distrusted only a cross-currency 1.0, which would still have let the 24x case straight through —
 * so the field is simply not read for currency any more.
 *
 * What remains: an explicit `rate_in_base`, which is a genuine currency claim (the live wire sends
 * none today). Otherwise `null`, which is what routes the offer to the `fx` snapshot arriving in
 * the same `get_comparison` payload — `fx.USD_AED = 3.65`, from the `Currency Exchange` doctype —
 * via `compare.ts`'s `fxLookup`. An offer with neither is EXCLUDED with a stated reason rather
 * than ranked on its raw number.
 *
 * The UOM factor is still used, for the thing it actually is: `compare.ts` restates rate and stock
 * into the RFQ's unit through its own `factor`.
 */
function baseAmount(stated: unknown): number | null {
  return typeof stated === 'number' && Number.isFinite(stated) && stated > 0 ? stated : null
}

export function toOffer(
  w: WireComparisonOffer | null | undefined,
  supplier: string,
  rfqUom: string,
  supplierCurrency: string,
): Offer | null {
  if (!w) return null
  const rate = num(w.rate)
  const currency = str(w.currency) || supplierCurrency
  const base = baseAmount(w.rate_in_base)
  return {
    supplier,
    supplier_quotation: str(w.supplier_quotation),
    // The offer's OWN currency when it has one. Falling back to the supplier's is what this file
    // used to do unconditionally, and it is only right while a supplier quotes in one currency.
    rate: money(rate, currency),
    stock: num(w.stock),
    remark: str(w.remark),
    rate_in_base: base === null ? null : money(base, getBaseCurrency()),
    delivery_date: str(w.delivery_date),
    rfq_uom: rfqUom,
    sq_uom: str(w.uom) || rfqUom,
    uom_mismatch: bool01(w.uom_mismatch),
  }
}

/** One row of the flat `offers[]` array → the same `Offer`. */
export function toFlatOffer(w: WireFlatOffer, rfqUom: string): Offer {
  const rate = num(w.rate)
  const currency = str(w.currency) || getBaseCurrency()
  const base = baseAmount(w.rate_in_base)
  return {
    supplier: str(w.supplier),
    supplier_quotation: str(w.supplier_quotation),
    rate: money(rate, currency),
    // `available_qty` is the newer name; `stock` the older. Read both so neither shape loses it.
    stock: num(w.available_qty ?? w.stock),
    remark: str(w.remark),
    rate_in_base: base === null ? null : money(base, getBaseCurrency()),
    delivery_date: str(w.delivery_date),
    rfq_uom: rfqUom,
    sq_uom: str(w.uom) || rfqUom,
    uom_mismatch: bool01(w.uom_mismatch),
  }
}

function toComparisonItem(
  w: WireComparisonItem,
  currencyBySupplier: Map<string, string>,
  /** Flat offers already grouped by `enquiry_row_no`; empty on the current shape. */
  flatByRow: Map<string, WireFlatOffer[]>,
): ComparisonItem {
  const rowNo = str(w.enquiry_row_no)
  const rfqUom = str(w.uom)
  const name = str(w.item_name) || str(w.item_code)
  const qty = num(w.qty)

  // Keyed on `enquiry_row_no`, never `item_code`: one enquiry can name the same item on two lines,
  // and joining on the code would give both lines every offer belonging to either.
  const flat = flatByRow.get(rowNo)
  const offers: Offer[] =
    flat !== undefined
      ? flat.map((offer) => toFlatOffer(offer, rfqUom))
      : Object.entries(w.offers ?? {})
          .map(([supplier, offer]) => toOffer(offer, supplier, rfqUom, currencyBySupplier.get(supplier) ?? getBaseCurrency()))
          // A supplier who did not quote this line is simply absent — the old `null` placeholder
          // existed only because the map needed one key per supplier column.
          .filter((offer): offer is Offer => offer !== null)

  return {
    item_code: str(w.item_code),
    name,
    qty,
    uom: rfqUom,
    enquiry_row_no: rowNo,
    // Single entry — see the module header's "one row per enquiry line" section. `cust_sno`/
    // `description` have no distinct source on this wire; `enquiry_row_no`/`item_name` stand
    // in, same honesty `useEnquiries.ts`'s `toEnquiryItem` uses for the same gap.
    lines: [{ line_id: rowNo, cust_sno: rowNo, description: name, qty }],
    offers,
  }
}

export function toAllocationRow(w: WireAllocationRow): AllocationRow {
  return {
    item_code: str(w.item_code),
    enquiry_row_no: str(w.enquiry_row_no),
    supplier: str(w.supplier),
    allocated_qty: num(w.allocated_qty),
    rate: num(w.rate),
    currency: str(w.currency, getBaseCurrency()),
  }
}

/**
 * The supplier columns, when the wire does not send a `suppliers[]` of its own.
 *
 * Derived from the offers themselves, deduped on `Supplier.code`. The server's own `suppliers[]`
 * is not that list — live it holds FIVE rows for one supplier, one per quotation, so it is really
 * a quotation list wearing a supplier's name. Deriving avoids inheriting that confusion.
 */
/** One column per supplier CODE, first occurrence winning. See `toComparison` for why. */
export function dedupeSuppliers(suppliers: readonly ComparisonSupplier[]): ComparisonSupplier[] {
  const seen = new Map<string, ComparisonSupplier>()
  for (const supplier of suppliers) if (!seen.has(supplier.supplier)) seen.set(supplier.supplier, supplier)
  return [...seen.values()]
}

export function suppliersFromOffers(offers: readonly WireFlatOffer[]): ComparisonSupplier[] {
  const bySupplier = new Map<string, ComparisonSupplier>()
  for (const offer of offers) {
    const supplier = str(offer.supplier)
    if (supplier === '' || bySupplier.has(supplier)) continue
    bySupplier.set(supplier, {
      supplier,
      supplier_name: trimmed(offer.supplier_name) || supplier,
      currency: str(offer.currency, getBaseCurrency()),
      sq: str(offer.supplier_quotation),
    })
  }
  return [...bySupplier.values()]
}

/**
 * Reads BOTH payload shapes into one `Comparison`.
 *
 * The presence of a top-level `offers` ARRAY selects the newer reader; the current server sends no
 * such key and falls through to the per-item map. This is a deliberate, tested branch rather than
 * leniency: the deployed server sends the old shape today, and hard-switching would blank the
 * workbench the moment this ships. Delete the map branch once the flat array is live everywhere.
 */
export function toComparison(w: WireComparison): Comparison {
  const flat = Array.isArray(w.offers) ? w.offers : null

  /*
    Deduped on `Supplier.code` in BOTH shapes. The server's `suppliers[]` is not a supplier list —
    live, ENQ-2026-00018 returns five entries all reading `AKA TRADE BUILDING TOOLS`, one per
    `Supplier Quotation`. Rendered straight, that produced five identical supplier COLUMNS in the
    workbench, each resolving through `bySupplier` to the very same offer: the same price shown five
    times as though five suppliers had quoted it.

    Deduping loses nothing the columns can express, because a supplier-keyed column cannot show two
    quotations from one supplier either. Making the columns per-QUOTATION is the next stage.
  */
  const suppliers = dedupeSuppliers(
    flat !== null ? suppliersFromOffers(flat) : (w.suppliers ?? []).map(toComparisonSupplier),
  )
  const currencyBySupplier = new Map(suppliers.map((s) => [s.supplier, s.currency]))

  const flatByRow = new Map<string, WireFlatOffer[]>()
  for (const offer of flat ?? []) {
    const row = str(offer.enquiry_row_no)
    const list = flatByRow.get(row) ?? []
    list.push(offer)
    flatByRow.set(row, list)
  }

  return {
    enquiry: str(w.enquiry),
    // Kept whenever sent, even alongside `rate_in_base`: it is what lets the client explain a
    // converted number, and `normaliseComparison` still needs it for any offer the server did not
    // pre-convert.
    fx: w.fx ?? {},
    suppliers,
    items: (w.items ?? []).map((item) => toComparisonItem(item, currencyBySupplier, flatByRow)),
    allocations: (w.allocations ?? []).map(toAllocationRow),
  }
}

/**
 * Polls by default, so a supplier quotation landing while the workbench is open shows up
 * without a manual reload.
 *
 * This refreshes the OFFERS and everything derived from them (coverage, best-per-item, the four
 * strategy previews). It deliberately does NOT touch the user's manual split: `ComparePage.tsx`
 * seeds `allocations` exactly once and early-returns while it is non-null. A poll that drops a
 * supplier the user had already allocated to therefore will not self-heal — accepted, see the
 * design's §6.
 */
export function useComparison(enquiryId: string | null | undefined, options?: { poll?: boolean }) {
  return useQuery({
    queryKey: qk.comparison(enquiryId ?? ''),
    queryFn: async () =>
      toComparison(await callGet<WireComparison>('comparison', 'get_comparison', { enquiry: enquiryId })),
    enabled: !!enquiryId,
    refetchInterval: options?.poll === false ? false : POLL_MS,
  })
}

/* ============================================================================================
 * comparison.save_comparison (PUT) → useSaveAllocation
 * ============================================================================================ */

interface WireSaveAllocationResult {
  saved?: boolean
  allocations?: WireAllocationRow[]
  /** The `Supplier Quote Comparison` this allocation was written to — the quotation records it as
   *  its own provenance, so the caller needs it back. */
  comparison?: string
}

function toWireAllocationRow(row: AllocationRow) {
  return {
    item_code: row.item_code,
    enquiry_row_no: row.enquiry_row_no,
    supplier: row.supplier,
    allocated_qty: row.allocated_qty,
    rate: row.rate,
    currency: row.currency,
  }
}

export interface SaveAllocationResult {
  allocations: AllocationRow[]
  /** `''` when the server did not name one — the quotation is still creatable without it. */
  comparison: string
}

/**
 * Make sure the enquiry HAS a `Supplier Quote Comparison` before the allocation is saved into it.
 *
 * `save_comparison` creates that document when one is missing, and creates it WITHOUT a `company` —
 * which is `reqd` on the doctype, so the whole save dies:
 *
 *   POST comparison.save_comparison {"enquiry":"ENQ-2026-00010","allocations":[…]}
 *     → MandatoryError: Value missing for Supplier Quote Comparison: Company
 *
 * Passing `company` in the request body does not help — measured at top level, under `data`, and
 * under `comparison`; the method ignores all three. What DOES work is the document already
 * existing: the save then takes its update path and never runs the create.
 *
 * So we create it, with the company the enquiry already carries — the same source
 * `resolveEnquiryCompany` uses for `create_rfq`, so a comparison and its RFQs can never disagree
 * about the legal entity. Idempotent: an enquiry that already has one is left alone.
 *
 * Delete this the day `save_comparison` defaults `company` itself (ask `B6`); nothing else changes.
 */
async function ensureComparisonDocument(enquiryId: string): Promise<void> {
  const existing = await getResource<{ name?: string }>('Supplier Quote Comparison', {
    filters: [['enquiry', '=', enquiryId]],
    fields: ['name'],
    limit: 1,
  })
  if (existing.data.length > 0) return

  // `enquiry` and `company` are the doctype's only mandatory fields — measured, both `reqd: 1`.
  // Note the fieldname is `enquiry`, NOT `enquiry_no`, which fails with a mandatory error naming a
  // field the caller thought it had supplied.
  await createResource('Supplier Quote Comparison', {
    enquiry: enquiryId,
    company: await resolveEnquiryCompany(enquiryId),
  })
}

async function saveAllocation(enquiryId: string, allocations: AllocationRow[]): Promise<SaveAllocationResult> {
  await ensureComparisonDocument(enquiryId)
  /*
    POST, not PUT. Measured 2026-08-08 against the live server:

      PUT  comparison.save_comparison -> PermissionError: Not permitted   (verb rejected at routing)
      POST comparison.save_comparison -> MandatoryError: ... company      (reached the business logic)

    So the allocation save has NEVER reached the server — which is why every enquiry on the bench
    reads `allocations: []`, and why the comparison -> quotation path has never run end to end.
    The `PermissionError` reads like an authorisation problem and is not one; it is Frappe
    refusing the HTTP verb for a whitelisted method that only allows POST.
  */
  const w = await callPost<WireSaveAllocationResult>('comparison', 'save_comparison', {
    enquiry: enquiryId,
    allocations: allocations.map(toWireAllocationRow),
  })
  return { allocations: (w.allocations ?? []).map(toAllocationRow), comparison: str(w.comparison) }
}

/**
 * A full replace — the body is the whole chosen split, not a patch (contract §8).
 *
 * `currentStatus` is the enquiry's status as the CALLER last read it. Omit it and the status write
 * is skipped entirely — the allocation still saves, so a caller with no enquiry in hand is not
 * forced to fetch one.
 */
export function useSaveAllocation(
  enquiryId: string,
  options?: {
    currentStatus?: EnquiryStatus
    onSuccess?: () => void
    onError?: (error: BosunApiError) => void
  },
) {
  const queryClient = useQueryClient()
  const currentStatus = options?.currentStatus
  return useMutation<SaveAllocationResult, BosunApiError, AllocationRow[]>({
    mutationFn: async (allocations) => {
      const saved = await saveAllocation(enquiryId, allocations)
      if (currentStatus) await advanceToComparisonDone(enquiryId, currentStatus)
      return saved
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.comparison(enquiryId) })
      // The status write above lands on the enquiry, not the comparison — the header badge on
      // this screen and the Requirements board both read those keys, so both need telling.
      void queryClient.invalidateQueries({ queryKey: qk.enquiry(enquiryId) })
      void queryClient.invalidateQueries({ queryKey: qk.enquiriesRoot() })
      options?.onSuccess?.()
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ============================================================================================
 * comparison.build_quotation → useBuildQuotation
 * ============================================================================================ */

interface WireBuildQuotationResult {
  quotation?: string
  /** `false` when an existing quotation was returned rather than composed fresh from the
   * CURRENT allocation — see `comparison.ts`'s own comment on this branch. Missing/non-boolean
   * reads as `true` (freshly built) only for a response that predates this field; every mock
   * response after this fix always sends it explicitly. */
  created?: boolean
}

export interface BuildQuotationResult {
  quotation: string
  /** `false` means the document on screen next is an EARLIER allocation's prices, not this
   * one's — `ComparePage` must tell the purchaser rather than navigate there silently. */
  created: boolean
}

async function buildQuotation(enquiryId: string): Promise<BuildQuotationResult> {
  const w = await callPost<WireBuildQuotationResult>('comparison', 'build_quotation', { enquiry: enquiryId })
  return { quotation: str(w.quotation), created: w.created !== false }
}

export function useBuildQuotation(options?: {
  onSuccess?: (data: BuildQuotationResult, enquiryId: string) => void
  onError?: (error: BosunApiError) => void
}) {
  const queryClient = useQueryClient()
  return useMutation<BuildQuotationResult, BosunApiError, string>({
    mutationFn: buildQuotation,
    onSuccess: (data, enquiryId) => {
      void queryClient.invalidateQueries({ queryKey: qk.quotationForEnquiry(enquiryId) })
      options?.onSuccess?.(data, enquiryId)
    },
    onError: (error) => options?.onError?.(error),
  })
}

/* ---------- test-only export ---------- */

export const __test__ = { saveAllocation, buildQuotation }
